import HttpService from "./HttpService";
import UserService from "../services/UserService";

class OcrService extends HttpService {
  constructor() {
    const userService = new UserService();
    const user = userService.getUser();
    super();
    this.userId = user?.id || null;
    this.uploadBaseUrl = process.env.REACT_APP_OCR_UPLOAD_BASE_URL;
    this.sendBaseUrl = process.env.REACT_APP_OCR_SEND_BASE_URL;
    this.apiKey = process.env.REACT_APP_OCR_API_KEY;
    this.timeoutMs = Number(process.env.REACT_APP_OCR_TIMEOUT_MS) || 20000;
    this.statusRetries = Number(process.env.REACT_APP_OCR_STATUS_RETRIES) || 2;
    this.retryDelayMs = Number(process.env.REACT_APP_OCR_RETRY_DELAY_MS) || 700;
  }

  getBaseConfig() {
    return {
      timeout: this.timeoutMs,
      headers: {
        "X-API-Key": this.apiKey,
      },
    };
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  shouldRetryStatus(error, attempt) {
    if (attempt >= this.statusRetries) {
      return false;
    }

    const status = error?.response?.status;
    const code = error?.code;

    if (status >= 500 || status === 429 || status === 408) {
      return true;
    }

    return (
      code === "ECONNABORTED" ||
      code === "ETIMEDOUT" ||
      code === "ERR_NETWORK"
    );
  }

  getErrorMessage(error, operation) {
    const status = error?.response?.status;
    const code = error?.code;

    if (status === 401 || status === 403) {
      return `${operation}: autenticação inválida na API OCR.`;
    }

    if (status === 400) {
      return `${operation}: dados inválidos para processamento OCR.`;
    }

    if (status === 404) {
      return `${operation}: documento OCR não encontrado.`;
    }

    if (status === 408 || code === "ECONNABORTED" || code === "ETIMEDOUT") {
      return `${operation}: tempo limite excedido ao comunicar com serviço OCR.`;
    }

    if (status === 429) {
      return `${operation}: serviço OCR indisponível no momento (limite de requisições).`;
    }

    if (status >= 500) {
      return `${operation}: falha temporária no serviço OCR.`;
    }

    if (code === "ERR_NETWORK") {
      return `${operation}: falha de rede ao comunicar com serviço OCR.`;
    }

    if (error?.message) {
      return `${operation}: ${error.message}`;
    }

    return `${operation}: erro inesperado na integração OCR.`;
  }

  async sendDocument(data) {
    try {
      const response = await this.post(this.sendBaseUrl, data, this.getBaseConfig());
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, "Falha ao enviar documento"));
    }
  }

  async uploadDocument(data) {
    try {
      const payload = { ...data, user_id: this.userId };
      const response = await this.post(
        `${this.uploadBaseUrl}`,
        payload,
        this.getBaseConfig()
      );
      return response.data;
    } catch (error) {
      throw new Error(this.getErrorMessage(error, "Falha no upload de documento"));
    }
  }

  async checkDocumentStatus(id) {
    let attempt = 0;

    while (true) {
      try {
        const response = await this.get(
          `${this.uploadBaseUrl}/${id}`,
          this.getBaseConfig()
        );
        return response.data;
      } catch (error) {
        if (!this.shouldRetryStatus(error, attempt)) {
          throw new Error(
            this.getErrorMessage(error, "Falha ao consultar status do documento")
          );
        }

        attempt += 1;
        await this.sleep(this.retryDelayMs * attempt);
      }
    }
  }
}

export default OcrService;
